import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet,
  Printer,
  Search,
  Filter,
  CheckCircle,
  AlertTriangle,
  Target
} from 'lucide-react';
import { format } from 'date-fns';

export default function RentabilidadeReportPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [rentabilidadeData, setRentabilidadeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [ordenarPor, setOrdenarPor] = useState('vehicle_id');
  const [ordem, setOrdem] = useState('crescente');
  const [periodoFilter, setPeriodoFilter] = useState('historico');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAndSortData();
  }, [rentabilidadeData, searchTerm, ordenarPor, ordem]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load vehicles
      const vehiclesResponse = await axios.get(`${API}/vehicles`, { withCredentials: true });
      const vehiclesList = vehiclesResponse.data;
      setVehicles(vehiclesList);
      
      // Load all paid invoices for revenue (ALL HISTORY - for historical profitability)
      const invoicesResponse = await axios.get(`${API}/invoices?status=paga`, { withCredentials: true });
      const paidInvoices = invoicesResponse.data;
      
      // Load all maintenances for expenses (ALL HISTORY)
      const maintenancesResponse = await axios.get(`${API}/maintenances`, { withCredentials: true });
      const maintenances = maintenancesResponse.data;
      
      // Load all transactions (receitas AND despesas)
      const transactionsResponse = await axios.get(`${API}/transactions`, { withCredentials: true });
      const allTransactions = transactionsResponse.data;
      
      // Load expenses from expenses collection
      const expensesCollectionResponse = await axios.get(`${API}/expenses`, { withCredentials: true });
      const expensesCollection = expensesCollectionResponse.data;
      
      // Calculate rentabilidade per vehicle
      const rentabilidade = vehiclesList.map(vehicle => {
        // Revenue from invoices (using valor_pago which includes penalties)
        const vehicleInvoices = paidInvoices.filter(inv => inv.vehicle_id === vehicle.vehicle_id);
        const invoiceRevenue = vehicleInvoices.reduce((acc, inv) => 
          acc + (inv.valor_pago || inv.valor_atualizado || inv.valor_original || 0), 0);
        
        // Revenue from transactions (vehicle-specific, without invoice_id to avoid double counting)
        const vehicleRevenueTransactions = allTransactions.filter(t => 
          t.vehicle_id === vehicle.vehicle_id && 
          t.tipo === 'receita' &&
          !t.invoice_id  // Exclude transactions generated from invoices
        );
        const transactionRevenue = vehicleRevenueTransactions.reduce((acc, t) => acc + (t.valor || 0), 0);
        
        const totalReceitas = invoiceRevenue + transactionRevenue;
        
        // Expenses from maintenances
        const vehicleMaintenances = maintenances.filter(m => m.vehicle_id === vehicle.vehicle_id);
        const maintenanceCosts = vehicleMaintenances.reduce((acc, m) => acc + (m.valor || 0), 0);
        
        // Expenses from transactions (vehicle-specific, without invoice_id to avoid double counting)
        const vehicleExpenseTransactions = allTransactions.filter(t => 
          t.vehicle_id === vehicle.vehicle_id && 
          t.tipo === 'despesa' &&
          !t.invoice_id  // Exclude transactions generated from invoices
        );
        const transactionExpenses = vehicleExpenseTransactions.reduce((acc, t) => acc + (t.valor || 0), 0);
        
        // Expenses from expenses collection
        const vehicleExpensesCollection = expensesCollection.filter(e => e.vehicle_id === vehicle.vehicle_id);
        const collectionExpenses = vehicleExpensesCollection.reduce((acc, e) => acc + (e.valor || 0), 0);
        
        const totalDespesas = maintenanceCosts + transactionExpenses + collectionExpenses;
        const lucroPrejuizo = totalReceitas - totalDespesas;
        
        // Count contracts
        const numContratos = vehicleInvoices.length > 0 ? 
          [...new Set(vehicleInvoices.map(i => i.contract_id))].length : 0;
        
        // Calculate % Payback based on FIPE value
        const valorFipe = vehicle.valor_fipe || 0;
        const paybackPercent = valorFipe > 0 ? (lucroPrejuizo / valorFipe) * 100 : 0;
        
        return {
          vehicle_id: vehicle.vehicle_id,
          placa: vehicle.placa,
          marca_modelo: `${vehicle.marca} ${vehicle.modelo}`,
          valor_fipe: valorFipe,
          total_receitas: totalReceitas,
          total_despesas: totalDespesas,
          lucro_prejuizo: lucroPrejuizo,
          num_contratos: numContratos,
          payback_percent: paybackPercent,
          status: lucroPrejuizo >= 0 ? 'lucro' : 'prejuizo'
        };
      });
      
      setRentabilidadeData(rentabilidade);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados de rentabilidade');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortData = () => {
    let result = [...rentabilidadeData];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.vehicle_id?.toLowerCase().includes(term) ||
        r.placa?.toLowerCase().includes(term) ||
        r.marca_modelo?.toLowerCase().includes(term)
      );
    }

    // Alphanumeric sort function for vehicle IDs
    const parseId = (id) => {
      if (!id) return [0, 0];
      const parts = String(id).split('.');
      const main = parseInt(parts[0].replace(/\D/g, '')) || 0;
      const sub = parts[1] ? parseInt(parts[1]) : 0;
      return [main, sub];
    };

    // Sort
    if (ordenarPor === 'vehicle_id') {
      // Alphanumeric sort for vehicle ID
      result.sort((a, b) => {
        const [aMain, aSub] = parseId(a.vehicle_id);
        const [bMain, bSub] = parseId(b.vehicle_id);
        
        let comparison = 0;
        if (aMain !== bMain) comparison = aMain - bMain;
        else comparison = aSub - bSub;
        
        return ordem === 'crescente' ? comparison : -comparison;
      });
    } else {
      result.sort((a, b) => {
        let comparison = 0;
        switch (ordenarPor) {
          case 'lucro_prejuizo':
            comparison = a.lucro_prejuizo - b.lucro_prejuizo;
            break;
          case 'receitas':
            comparison = a.total_receitas - b.total_receitas;
            break;
          case 'despesas':
            comparison = a.total_despesas - b.total_despesas;
            break;
          case 'payback':
            comparison = a.payback_percent - b.payback_percent;
            break;
          default:
            // Default: sort by vehicle_id alphanumerically
            const [aMain, aSub] = parseId(a.vehicle_id);
            const [bMain, bSub] = parseId(b.vehicle_id);
            if (aMain !== bMain) comparison = aMain - bMain;
            else comparison = aSub - bSub;
        }
        return ordem === 'crescente' ? comparison : -comparison;
      });
    }

    setFilteredData(result);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calculate totals
  const totalReceitas = filteredData.reduce((acc, r) => acc + r.total_receitas, 0);
  const totalDespesas = filteredData.reduce((acc, r) => acc + r.total_despesas, 0);
  const lucroTotal = totalReceitas - totalDespesas;
  const vehiclesComLucro = filteredData.filter(r => r.status === 'lucro').length;
  const vehiclesComPrejuizo = filteredData.filter(r => r.status === 'prejuizo').length;
  
  // Critical vehicles - those with significant losses (prejuizo > R$ 1000 or negative payback > 10%)
  const criticalVehicles = filteredData.filter(r => 
    r.status === 'prejuizo' && (r.lucro_prejuizo < -1000 || r.payback_percent < -10)
  ).sort((a, b) => a.lucro_prejuizo - b.lucro_prejuizo);
  
  // Vehicles needing attention (prejuizo but not critical yet)
  const attentionVehicles = filteredData.filter(r => 
    r.status === 'prejuizo' && r.lucro_prejuizo >= -1000 && r.payback_percent >= -10
  );

  const exportToCSV = () => {
    const headers = ['ID Veículo', 'Placa', 'Marca/Modelo', 'Contratos', 'Receitas', 'Despesas', 'Lucro/Prejuízo', '% Payback', 'Status'];
    const rows = filteredData.map(r => [
      r.vehicle_id,
      r.placa,
      r.marca_modelo,
      r.num_contratos,
      r.total_receitas,
      r.total_despesas,
      r.lucro_prejuizo,
      r.payback_percent.toFixed(2),
      r.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rentabilidade_veiculos_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exportado!');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="rentabilidade-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-[Manrope]">Rentabilidade de Veículos</h1>
          <p className="text-gray-500 text-sm">Análise de lucro e prejuízo por veículo</p>
        </div>
        
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Exportar CSV
          </Button>
          <Button size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filtros e Ordenação</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Buscar Veículo</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="ID, Placa, Marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="rentabilidade-search"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Ordenar Por</Label>
              <Select value={ordenarPor} onValueChange={setOrdenarPor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicle_id">ID do Veículo</SelectItem>
                  <SelectItem value="lucro_prejuizo">Lucro/Prejuízo</SelectItem>
                  <SelectItem value="receitas">Receitas</SelectItem>
                  <SelectItem value="despesas">Despesas</SelectItem>
                  <SelectItem value="payback">% Payback</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Ordem</Label>
              <Select value={ordem} onValueChange={setOrdem}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="decrescente">Decrescente</SelectItem>
                  <SelectItem value="crescente">Crescente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Período</Label>
              <Select value={periodoFilter} onValueChange={setPeriodoFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="historico">Todo Histórico</SelectItem>
                  <SelectItem value="mes_atual">Mês Atual</SelectItem>
                  <SelectItem value="ano_atual">Ano Atual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-t-4 border-t-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Receitas</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalReceitas)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-t-4 border-t-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Despesas</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDespesas)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-t-4 border-t-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Lucro Total</p>
                <p className={`text-2xl font-bold ${lucroTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {lucroTotal >= 0 ? '' : '-'}{formatCurrency(Math.abs(lucroTotal))}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-t-4 border-t-purple-500">
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-gray-500">Status da Frota</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  {vehiclesComLucro} com lucro
                </span>
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                  {vehiclesComPrejuizo} em prejuízo
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section - Critical Vehicles */}
      {criticalVehicles.length > 0 && (
        <Card className="bg-red-50 border-red-200 print:hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-800">Alertas de Prejuízo Crítico</h3>
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                {criticalVehicles.length} veículo(s)
              </span>
            </div>
            <p className="text-sm text-red-700 mb-3">
              Veículos com prejuízo superior a R$ 1.000 ou payback negativo maior que 10%:
            </p>
            <div className="space-y-2">
              {criticalVehicles.slice(0, 5).map(v => (
                <div key={v.vehicle_id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-red-100 text-red-700 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="font-semibold text-gray-800">{v.vehicle_id}</p>
                      <p className="text-xs text-gray-500">{v.marca_modelo}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{formatCurrency(v.lucro_prejuizo)}</p>
                    <p className="text-xs text-red-500">{v.payback_percent.toFixed(1)}% payback</p>
                  </div>
                </div>
              ))}
              {criticalVehicles.length > 5 && (
                <p className="text-sm text-red-600 text-center">
                  + {criticalVehicles.length - 5} veículo(s) em situação crítica
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attention Section - Minor Losses */}
      {attentionVehicles.length > 0 && (
        <Card className="bg-yellow-50 border-yellow-200 print:hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-yellow-800">Veículos em Atenção</h3>
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                {attentionVehicles.length} veículo(s)
              </span>
            </div>
            <p className="text-sm text-yellow-700 mb-2">
              Veículos com pequeno prejuízo que precisam de acompanhamento:
            </p>
            <div className="flex flex-wrap gap-2">
              {attentionVehicles.slice(0, 10).map(v => (
                <span key={v.vehicle_id} className="px-3 py-1 bg-white border border-yellow-200 rounded-full text-sm">
                  <span className="font-medium text-gray-800">{v.vehicle_id}</span>
                  <span className="text-yellow-600 ml-1">({formatCurrency(v.lucro_prejuizo)})</span>
                </span>
              ))}
              {attentionVehicles.length > 10 && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                  +{attentionVehicles.length - 10} mais
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum dado encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">ID Veículo</TableHead>
                    <TableHead className="font-semibold">Placa</TableHead>
                    <TableHead className="font-semibold">Marca/Modelo</TableHead>
                    <TableHead className="font-semibold text-center">Contratos</TableHead>
                    <TableHead className="font-semibold text-right">Receitas</TableHead>
                    <TableHead className="font-semibold text-right">Despesas</TableHead>
                    <TableHead className="font-semibold text-right">Lucro/Prejuízo</TableHead>
                    <TableHead className="font-semibold text-right">% Payback</TableHead>
                    <TableHead className="font-semibold text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((row) => {
                    const isCritical = row.status === 'prejuizo' && (row.lucro_prejuizo < -1000 || row.payback_percent < -10);
                    return (
                    <TableRow 
                      key={row.vehicle_id} 
                      className={`hover:bg-gray-50 ${isCritical ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`}
                    >
                      <TableCell className="font-medium">
                        {isCritical && <AlertTriangle className="w-4 h-4 text-red-500 inline mr-1" />}
                        {row.vehicle_id}
                      </TableCell>
                      <TableCell>{row.placa}</TableCell>
                      <TableCell>{row.marca_modelo}</TableCell>
                      <TableCell className="text-center">{row.num_contratos}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(row.total_receitas)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(row.total_despesas)}</TableCell>
                      <TableCell className={`text-right font-bold ${row.lucro_prejuizo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.lucro_prejuizo)}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${row.payback_percent >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {row.payback_percent.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-center">
                        {row.status === 'lucro' ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">Lucro</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs">Prejuízo</span>
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
